import { useRef, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Stack,
  CircularProgress,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Mic as MicIcon,
  Videocam as VideocamIcon,
  QrCode2 as QrCodeIcon
} from '@mui/icons-material';
import IconFaceFrame from 'assets/icon_face_frame';
import { VoiceActivityAutoDetector } from 'components/Interview/VoiceActivityAutoDetector';
import VideoPreview from 'components/Interview/VideoPreview';
import QrCodeModal from './QrCodeModal';

export interface MediaDeviceInfo {
  deviceId: string;
  kind: string;
  label: string;
  groupId: string;
}

export interface DeviceCheckResult {
  microphone: {
    available: boolean;
    devices: MediaDeviceInfo[];
    hasPermission: boolean;
  };
  camera: {
    available: boolean;
    devices: MediaDeviceInfo[];
    hasPermission: boolean;
    resolution?: { width: number; height: number };
    fps?: number;
  };
}

interface MediaDevicesCheckProps {
  onDevicesChecked?: (result: DeviceCheckResult) => void;
  /** Передаётся из CheckDevices — убирает дублирование useMediaDevices */
  devicesResult?: DeviceCheckResult | null;
}

const MediaDevicesCheck = ({
  onDevicesChecked,
  devicesResult: devicesResultProp
}: MediaDevicesCheckProps) => {
  const devicesResult = devicesResultProp ?? null;
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoStreamRef = useRef<MediaStream | null>(null);
  const lastDevicesResultRef = useRef<DeviceCheckResult | null>(null);
  const [videoStream, setVideoStream] = useState<MediaStream | null>(null);
  const [isVideoLoading, setIsVideoLoading] = useState(false);
  const [selectedMicrophone, setSelectedMicrophone] = useState<string>('');
  const [selectedCamera, setSelectedCamera] = useState<string>('');
  const [qrOpen, setQrOpen] = useState(false);

  const theme = useTheme();
  // Телефон определяем по ширине экрана (< sm ≈ 600px) — на телефоне открывать
  // QR смысла нет, человек уже на мобильном устройстве.
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const isCameraAvailable = Boolean(
    devicesResult?.camera.hasPermission && devicesResult.camera.devices.length > 0
  );
  // Кнопку «Открыть на телефоне» показываем, только если камера недоступна
  // и при этом мы не на телефоне.
  const showOpenOnPhone = !isCameraAvailable && !isPhone;

  const getDeviceName = (device: MediaDeviceInfo, defaultName: string) => {
    if (!device.label) {
      return defaultName;
    }

    return device.label;
  };

  const handleMicrophoneChange = (deviceId: string) => {
    setSelectedMicrophone(deviceId);
    localStorage.setItem('selectedMicrophone', deviceId);
  };

  const handleCameraChange = (deviceId: string) => {
    setSelectedCamera(deviceId);
    localStorage.setItem('selectedCamera', deviceId);
  };

  useEffect(() => {
    if (devicesResult && onDevicesChecked && devicesResult !== lastDevicesResultRef.current) {
      lastDevicesResultRef.current = devicesResult;
      onDevicesChecked(devicesResult);
    }
  }, [devicesResult, onDevicesChecked]);

  // Инициализируем выбранные устройства при первой загрузке
  useEffect(() => {
    if (devicesResult && !selectedMicrophone && devicesResult.microphone.devices.length > 0) {
      const deviceId = devicesResult.microphone.devices[0].deviceId;
      setSelectedMicrophone(deviceId);
      localStorage.setItem('selectedMicrophone', deviceId);
    }
    if (devicesResult && !selectedCamera && devicesResult.camera.devices.length > 0) {
      const deviceId = devicesResult.camera.devices[0].deviceId;
      setSelectedCamera(deviceId);
      localStorage.setItem('selectedCamera', deviceId);
    }
  }, [devicesResult, selectedMicrophone, selectedCamera]);

  // Загружаем сохраненные устройства при инициализации
  useEffect(() => {
    const savedMicrophone = localStorage.getItem('selectedMicrophone');
    const savedCamera = localStorage.getItem('selectedCamera');

    if (savedMicrophone) {
      setSelectedMicrophone(savedMicrophone);
    }
    if (savedCamera) {
      setSelectedCamera(savedCamera);
    }
  }, []);

  // запрашиваем стрим только при смене разрешений или выбранной камеры
  useEffect(() => {
    if (devicesResult?.camera.hasPermission) {
      const startVideoStream = async () => {
        // Останавливаем предыдущий поток
        if (videoStreamRef.current) {
          videoStreamRef.current.getTracks().forEach((track) => track.stop());
        }

        setIsVideoLoading(true);
        try {
          // Пробуем получить максимально возможное качество для предварительного просмотра
          // Если камера ещё не выбрана (первый заход), используем общие ограничения без deviceId
          const videoConstraints = selectedCamera
            ? [
                // Попытка 1: Максимальное качество с конкретным устройством
                {
                  video: {
                    deviceId: { exact: selectedCamera },
                    width: { ideal: 1920, min: 1280 },
                    height: { ideal: 1080, min: 720 },
                    frameRate: { ideal: 30, min: 25 }
                  },
                  audio: false
                },
                // Попытка 2: HD качество с конкретным устройством
                {
                  video: {
                    deviceId: { exact: selectedCamera },
                    width: { ideal: 1280, min: 1024 },
                    height: { ideal: 720, min: 576 },
                    frameRate: { ideal: 30, min: 20 }
                  },
                  audio: false
                },
                // Попытка 3: Базовое качество с конкретным устройством
                {
                  video: {
                    deviceId: { exact: selectedCamera },
                    width: { ideal: 1280 },
                    height: { ideal: 720 },
                    frameRate: { ideal: 30 }
                  },
                  audio: false
                },
                // Попытка 4: Любое качество с конкретным устройством
                {
                  video: {
                    deviceId: { exact: selectedCamera }
                  },
                  audio: false
                }
              ]
            : [
                // Попытка 1: Максимальное качество без привязки к устройству
                {
                  video: {
                    width: { ideal: 1920, min: 1280 },
                    height: { ideal: 1080, min: 720 },
                    frameRate: { ideal: 30, min: 25 }
                  },
                  audio: false
                },
                // Попытка 2: HD качество
                {
                  video: {
                    width: { ideal: 1280, min: 1024 },
                    height: { ideal: 720, min: 576 },
                    frameRate: { ideal: 30, min: 20 }
                  },
                  audio: false
                },
                // Попытка 3: Базовое качество
                {
                  video: {
                    width: { ideal: 1280 },
                    height: { ideal: 720 },
                    frameRate: { ideal: 30 }
                  },
                  audio: false
                },
                // Попытка 4: Любое доступное качество
                {
                  video: true,
                  audio: false
                }
              ];

          let stream: MediaStream | null = null;
          let bestSettings: MediaTrackSettings | null = null;

          // Пробуем каждую конфигурацию
          for (const constraints of videoConstraints) {
            try {
              stream = await navigator.mediaDevices.getUserMedia(constraints);
              const videoTrack = stream.getVideoTracks()[0];
              if (videoTrack) {
                const settings = videoTrack.getSettings();
                // Если это первая успешная попытка или качество лучше предыдущего
                if (
                  !bestSettings ||
                  (settings.width &&
                    settings.height &&
                    settings.width * settings.height >
                      (bestSettings.width || 0) * (bestSettings.height || 0))
                ) {
                  bestSettings = settings;
                  // Если получили хорошее качество, останавливаем поиск
                  if (
                    settings.width &&
                    settings.height &&
                    settings.width * settings.height >= 1280 * 720
                  ) {
                    break;
                  }
                }
              }
            } catch (err) {
              // Продолжаем с следующей конфигурацией
              continue;
            }
          }

          if (stream) {
            console.log('Камера доступна, стрим получен:', stream);
            console.log('Настройки видео:', bestSettings);
            videoStreamRef.current = stream;
            setVideoStream(stream);

            // Если камера не была выбрана (первый запуск), пробуем сохранить deviceId из настроек трека
            if (!selectedCamera) {
              try {
                const track = stream.getVideoTracks()[0];
                const settings = track?.getSettings() as MediaTrackSettings & { deviceId?: string };
                if (settings?.deviceId) {
                  setSelectedCamera(settings.deviceId);
                  localStorage.setItem('selectedCamera', settings.deviceId);
                }
              } catch (_) {
                // ignore
              }
            }
          } else {
            throw new Error('Не удалось получить доступ к камере');
          }
        } catch (err) {
          console.error('Ошибка при запуске видео-потока:', err);
        } finally {
          setIsVideoLoading(false);
        }
      };

      startVideoStream();
    }

    return () => {
      if (videoStreamRef.current) {
        console.log('Очищаем стрим');
        videoStreamRef.current.getTracks().forEach((track) => track.stop());
        videoStreamRef.current = null;
      }
    };
  }, [devicesResult?.camera.hasPermission, selectedCamera]);

  // Подключаем стрим к video элементу. Сначала обнуляем srcObject — на iOS
  // элемент может «залипнуть» на старом стриме. setTimeout для play() — иначе серый экран.
  useEffect(() => {
    const currentVideoRef = videoRef.current;
    if (currentVideoRef && videoStream) {
      currentVideoRef.srcObject = null;
      currentVideoRef.srcObject = videoStream;
      const playTimeout = setTimeout(() => {
        currentVideoRef.play().catch((err) => {
          console.warn('Не удалось воспроизвести видео:', err);
        });
      }, 100);
      return () => {
        clearTimeout(playTimeout);
        currentVideoRef.srcObject = null;
      };
    }

    return () => {
      if (currentVideoRef) {
        currentVideoRef.srcObject = null;
      }
    };
  }, [videoStream]);

  return (
    <Stack spacing={0}>
      <Grid container spacing={4} justifyContent="center">
        {/* Микрофон */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent sx={{ p: 4, pb: '16px !important' }}>
              <Stack spacing={3}>
                <Stack direction="row" alignItems="center" spacing={3}>
                  <MicIcon color={devicesResult?.microphone.hasPermission ? 'success' : 'error'} />
                  {devicesResult?.microphone.hasPermission &&
                  devicesResult.microphone.devices.length > 0 ? (
                    <Typography variant="caption" color="text.secondary">
                      {getDeviceName(devicesResult.microphone.devices[0], 'Микрофон по умолчанию')}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="error">
                      Недоступен
                    </Typography>
                  )}
                </Stack>
                {devicesResult?.microphone.devices &&
                  devicesResult.microphone.devices.length > 1 && (
                    <FormControl fullWidth size="small">
                      <InputLabel>Выберите микрофон</InputLabel>
                      <Select
                        value={selectedMicrophone}
                        onChange={(e) => handleMicrophoneChange(e.target.value)}
                        label="Выберите микрофон"
                        size="small"
                      >
                        {devicesResult.microphone.devices.map((device) => (
                          <MenuItem key={device.deviceId} value={device.deviceId}>
                            {getDeviceName(device, 'Микрофон')}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
              </Stack>
              <Stack direction="row" alignItems="center" spacing={4} sx={{ mt: 3 }}>
                <Typography variant="body2">Уровень громкости:</Typography>
                <VoiceActivityAutoDetector
                  hasPermission={!!devicesResult?.microphone.hasPermission}
                />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Камера */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent sx={{ p: 4, pb: '16px !important' }}>
              <Stack direction="row" alignItems="center" spacing={3} sx={{ mb: 3 }}>
                <VideocamIcon color={devicesResult?.camera.hasPermission ? 'success' : 'error'} />
                {devicesResult?.camera.hasPermission && devicesResult.camera.devices.length > 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    {getDeviceName(devicesResult.camera.devices[0], 'Камера по умолчанию')}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="error">
                    Недоступна
                  </Typography>
                )}
                {showOpenOnPhone && (
                  <Box sx={{ mb: 3 }}>
                    <Button
                      variant="outlined"
                      startIcon={<QrCodeIcon />}
                      onClick={() => setQrOpen(true)}
                    >
                      Открыть на телефоне
                    </Button>
                  </Box>
                )}
              </Stack>

              {devicesResult?.camera.devices && devicesResult.camera.devices.length > 1 && (
                <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                  <InputLabel>Выберите камеру</InputLabel>
                  <Select
                    value={selectedCamera}
                    onChange={(e) => handleCameraChange(e.target.value)}
                    label="Выберите камеру"
                  >
                    {devicesResult.camera.devices.map((device) => (
                      <MenuItem key={device.deviceId} value={device.deviceId}>
                        {getDeviceName(device, 'Камера')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {isVideoLoading ? (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)'
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : null}

              <Stack spacing={2} alignItems="center">
                {/* Видео-предварительный просмотр */}
                <VideoPreview
                  ref={videoRef}
                  isPreview
                  isRecording
                  hasVideoAccess={devicesResult?.camera.hasPermission}
                />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <QrCodeModal open={qrOpen} onClose={() => setQrOpen(false)} url={window.location.href} />
    </Stack>
  );
};

export default MediaDevicesCheck;
